import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Button from '../../common/Button/Button';
import ScreenContainer from '../../common/ScreenContainer/ScreenContainer';
import TextField from '../../common/TextField/TextField';
import './ForgotPassword.css';
import { ReactComponent as SendIcon } from '../../../assets/send.svg';
import useCaptcha from '../../../customHooks/useCaptcha';
import { AppContext } from '../../../utils/AppContext';
import useAuthentication from '../../../customHooks/useAuthentication';
import { MixpanelContext, SCREENS } from '../../../utils/mixpanel';
import { isLocalEnv } from '../../../utils/env';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string>();
  const history = useHistory();
  const { logoURL } = useContext(AppContext);
  const { forgotPasswordEmail } = useAuthentication();
  const { t } = useTranslation();
  const { renderCaptcha, getCaptchaResponse, showCaptchaError, CaptchaComponent, isCaptchaError } =
    useCaptcha();
  const { sendMixpanelError, sendMixpanelEvent } = useContext(MixpanelContext);

  const isLocalEnvValue = useMemo(() => isLocalEnv(), []);

  useEffect(() => {
    renderCaptcha();
  }, [renderCaptcha]);

  const onForgotPassword = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (email === '') {
      setErrorMessageKey('emailEmpty');
      sendMixpanelError({
        email,
        errorKey: 'emailEmpty',
        screen: SCREENS.FORGOT_PASSWORD,
      });
      return;
    }

    setErrorMessageKey(undefined);

    const { response, shouldCheck } = getCaptchaResponse();
    if (shouldCheck && !response) {
      showCaptchaError();
      return;
    }

    setIsButtonAnimating(true);
    forgotPasswordEmail(email, response)
      .then(() => {
        setIsEmailSent(true);
        sendMixpanelEvent('Successfully moved to email sent screen', {
          email,
          screen: SCREENS.FORGOT_PASSWORD,
        });
      })
      .catch((axiosError) => {
        setErrorMessageKey('sendEmailError');
        sendMixpanelError({
          email,
          errorKey: 'sendEmailError',
          axiosError,
          screen: SCREENS.FORGOT_PASSWORD,
        });
      })
      .finally(() => {
        setIsButtonAnimating(false);
      });
  };

  const goToLoginScreen = () => {
    history.push(`/login${window.location.search}`);
  };

  const onInvalidEmail = () => {
    setErrorMessageKey('invalidEmail');
    sendMixpanelError({
      email,
      errorKey: 'invalidEmail',
      screen: SCREENS.FORGOT_PASSWORD,
    });
  };

  const screenContainerProps = !isEmailSent
    ? {
        logoURL,
        title: t('resetTitle'),
        subTitle: t('resetSubtitle'),
      }
    : {};

  return (
    <ScreenContainer
      bottomActionComponent={
        <button className="login-button" onClick={goToLoginScreen}>
          {t('login')}
        </button>
      }
      errorKey={errorMessageKey}
      {...screenContainerProps}
    >
      {!isEmailSent ? (
        <form className="forgot-password-form" onSubmit={onForgotPassword}>
          <TextField
            value={email}
            label={t('email')}
            placeholder={t('emailPlaceholder')}
            onChange={(e) => setEmail(e.currentTarget.value)}
            id="email"
            type="email"
            onInvalid={onInvalidEmail}
          ></TextField>
          {!isLocalEnvValue && <CaptchaComponent showCaptchaError={isCaptchaError} />}
          <Button text={t('resetButton')} isAnimating={isButtonAnimating}></Button>
        </form>
      ) : (
        <div className="email-sent-container">
          <SendIcon />
          <span className="email-sent-title">{t('resetLinkSent.title')}</span>
          <span className="email-sent-message">
            <Trans
              t={t}
              i18nKey="resetLinkSent.text"
              values={{ email }}
              components={{
                style: <span className="email-address" dir="ltr" />,
              }}
            ></Trans>
          </span>
        </div>
      )}
    </ScreenContainer>
  );
};

export default ForgotPassword;
